import { css } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { ColumnChooserParams, ColumnMenuVisibleChangedEvent } from 'ag-grid-community';
import { CustomCellRendererProps, CustomHeaderProps } from 'ag-grid-react';
import { CSSProperties, useCallback, useEffect, useRef } from 'react';
import { DescriptionIcon } from '../../../../../components/AgTable/cell-renderers/FieldNameRenderer';
import { formatIndicator, Indicator } from '../../../../../components/Indicator/Indicator';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import type { FinancialsGridRowData } from './FinancialsGrid2Defs';
import { CURRENCY_UNRESOLVED_TITLE } from './KpiCellRenderers';

/**
 * Inner renderer of the KPI tree cell; the group renderer around it draws the chevron and indent. `value` is the
 * column's value (`getCompanyProfileKpiTreeColDef`): the name on the actual row, the kind under it, the group name.
 */
export function FinancialsKpiNameRenderer({
  data,
  value,
}: CustomCellRendererProps<FinancialsGridRowData, string>) {
  if (data?.kind === KpiSection.actual) {
    return (
      <Stack component='span' direction='row' alignItems='center' gap={2}>
        <Typography variant='body2' noWrap component='span'>
          {value}
        </Typography>
        <DescriptionIcon description={data.formField.description ?? null} />
      </Stack>
    );
  }
  return (
    <Typography variant='body2' color={data ? 'text.secondary' : undefined} noWrap component='span'>
      {value}
    </Typography>
  );
}

export interface IFinancialsValueRendererParams extends CustomCellRendererProps<FinancialsGridRowData> {
  /** Both take the column's `field`, the row-data key of the cell, which names the period as well as the date. */
  isCurrencyUnresolved: (row: FinancialsGridRowData, field: string) => boolean;
  /** The cell's period-over-period delta; null when there is none to show. */
  getGrowth?: (row: FinancialsGridRowData, field: string) => number | null;
}

const deltaCellCss = css`
  display: flex;
  align-items: center;
  height: 100%;
`;
/** With less room than this beside the value, gap included, the chip would show little but its background, so it hides. */
const deltaChipMinRoom = '4rem';
// the room left beside the value: the chip gives way first, ellipsizing its number, then hides; only then the value ellipsizes
const deltaRoomCss = css`
  flex: 1 1 0;
  min-width: 0;
  container-type: inline-size;

  @container (max-width: ${deltaChipMinRoom}) {
    /* doubled to outrank the chip's own display */
    && > * {
      display: none;
    }
  }
`;
// the gap is the chip's own margin so that it hides along with the chip and leaves the value the whole cell
const deltaChipGap = '0.5rem';
const deltaChipStyle: CSSProperties = {
  lineHeight: '1.5rem',
  width: 'fit-content',
  maxWidth: `calc(100% - ${deltaChipGap})`,
  minWidth: 0,
  marginRight: deltaChipGap,
};
const deltaValueCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The formatted value, with its delta chip when the cell has one; a muted dash when the BE could not convert it. */
export function FinancialsValueRenderer({
  colDef,
  data,
  isCurrencyUnresolved,
  getGrowth,
  valueFormatted,
}: IFinancialsValueRendererParams) {
  const field = colDef?.field;
  const text = valueFormatted ?? '';
  if (!data || !field) {
    return <>{text}</>;
  }
  if (isCurrencyUnresolved(data, field)) {
    return (
      <Box component='span' color='text.disabled' title={CURRENCY_UNRESOLVED_TITLE}>
        —
      </Box>
    );
  }
  const growth = getGrowth?.(data, field) ?? null;
  if (growth === null) {
    return <>{text}</>;
  }
  return (
    <span css={deltaCellCss}>
      <span css={deltaRoomCss}>
        <Indicator indicator={growth} title={formatIndicator(growth)} style={deltaChipStyle} />
      </span>
      <span css={deltaValueCss}>{text}</span>
    </span>
  );
}

export const PINNED_YEARS_LABEL = 'Pinned years';

export interface IPinnedYearsChooserHeaderProps extends CustomHeaderProps {
  chooserParams: ColumnChooserParams;
}

/**
 * The header of the trailing "+" column: opens ag-grid's own column chooser over the pinned fiscal years.
 * Focus comes back here when that chooser closes, as it did from the legacy popup menu.
 */
export function PinnedYearsChooserHeader({ api, chooserParams }: IPinnedYearsChooserHeaderProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const ownChooserRef = useRef(false);

  const open = useCallback(() => {
    // after the call, not before: ag-grid closes a chooser that is already up first, and that close
    // event would otherwise clear the flag the dialog we are about to open needs
    api.showColumnChooser(chooserParams);
    ownChooserRef.current = true;
  }, [api, chooserParams]);

  useEffect(() => {
    const onMenuVisibleChanged = (event: ColumnMenuVisibleChangedEvent) => {
      if (event.key === 'columnChooser' && !event.visible && ownChooserRef.current) {
        ownChooserRef.current = false;
        buttonRef.current?.focus();
      }
    };
    api.addEventListener('columnMenuVisibleChanged', onMenuVisibleChanged);
    return () => {
      if (api.isDestroyed()) {
        return;
      }
      api.removeEventListener('columnMenuVisibleChanged', onMenuVisibleChanged);
      // switching to yearly takes this column away; the chooser must not outlive the years it lists
      ownChooserRef.current = false;
      api.hideColumnChooser();
    };
  }, [api]);

  return (
    <Stack width='100%' height='100%' alignItems='center' justifyContent='center'>
      <IconButton ref={buttonRef} onClick={open} title={PINNED_YEARS_LABEL} aria-label={PINNED_YEARS_LABEL}>
        <AddIcon color='secondary' />
      </IconButton>
    </Stack>
  );
}
